import React, {Fragment, useEffect, useState} from "react";
import {LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import axios from "axios";
import {DamageCarrier, DefensiveAbility, EntityMonstrousAbility, Monster, MonsterAttributes, MonsterDefensiveAbility, MonsterIntelligence, MonsterOffensiveAbility, MonsterType, MonstrousAbility, OffensiveAbility} from "../../server/monster";
import {Autocomplete,
    Box, Button, ButtonGroup, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, Grid, IconButton, InputLabel, Menu, MenuItem, Paper, Select, TextField, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import TuneIcon from "@mui/icons-material/Tune";
import UploadIcon from "@mui/icons-material/Upload";
import PrintIcon from "@mui/icons-material/Print";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {CreateMonstrousAbilityDialog} from "./components/CreateMonstrousAbilityDialog";
import {useMonsterCatalog} from "./contexts/MonsterCatalogContext";
import {MonsterIndexCard} from "./components/MonsterIndexCard";
import {MonsterManualEntry} from "./components/MonsterManualEntry";

export const monsterLoader = async ({params, request}: LoaderFunctionArgs): Promise<Monster | null> => {
    if (typeof params.id !== "undefined") {
        if (params.id == "new") {
            const typeIdStr = new URL(request.url).searchParams.get('typeId')
            if (typeIdStr) {
                const type = await axios.get(`/monsters/type/${typeIdStr}`).then(({data}) => data)
                return {id: 0, name: "", type, unique: false, dedicated_marshal: false}
            }
            return {id: 0, name: "", type: undefined, unique: false, dedicated_marshal: false}
        }
        return loadMonsterForId(parseInt(params.id));
    } else {
        return null
    }
}

export async function loadMonsterForId(id: number): Promise<any> {
    const apiEndpoint = "/monsters/monster/" + id
    return await axios.get(apiEndpoint).then(({data}) => {
        if (data.attributes == undefined) { data.attributes = {} as MonsterAttributes }
        return data;
    })
}

export function MonsterViewer() {
    const [monster, setMonster] = useState(useLoaderData() as Monster);
    const [editable, setEditable] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const {types, carriers, offensiveAbilities: abilityCatalog, defensiveAbilities: defensiveCatalog,
        monstrousAbilities: monstrousCatalog, intelligenceLevels, refreshMonstrousAbilities} = useMonsterCatalog()
    const [selectedAbility, setSelectedAbility] = React.useState<OffensiveAbility | null>(null);
    const [addQty, setAddQty] = React.useState(1);
    const [addRecharge, setAddRecharge] = React.useState(0);
    const [selectedDefensive, setSelectedDefensive] = React.useState<DefensiveAbility | null>(null);
    const [addDefQty, setAddDefQty] = React.useState(1);
    const [selectedMonstrous, setSelectedMonstrous] = React.useState<MonstrousAbility | null>(null);
    const [offDetailAbility, setOffDetailAbility] = React.useState<MonsterOffensiveAbility | null>(null);
    const [defDetailAbility, setDefDetailAbility] = React.useState<MonsterDefensiveAbility | null>(null);
    const [createMonstrousOpen, setCreateMonstrousOpen] = React.useState(false);
    const [printCardMenuAnchor, setPrintCardMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [imageUploading, setImageUploading] = useState(false);
    const nav = useNavigate()

    useEffect(() => {
        if (monster.name == '') {
            setIsNew(true)
            setEditable(true)
        }
    }, [])

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.ctrlKey && e.key === 'e') { e.preventDefault(); setEditable(prev => !prev) }
            else if (e.ctrlKey && e.key === 's') { e.preventDefault(); if (editable) saveMonster() }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editable])

    function headLabel() {
        if (isNew) {
            return "Monster"
        } else return monster.name
    }

    async function saveMonster() {
        if (isNew) {
            return createMonster()
        } else {
            const apiEndpoint = "/monsters/monsters/" + monster.id
            await axios.put(apiEndpoint, monster).then(({data}) => {
                setMonster(data)
                setEditable(false)
            })
        }
    }

    async function createMonster() {
        if(monster.name == ""){
            return null
        }
        console.log("creating Monster")
        console.log(monster)
        const apiEndpoint = "/monsters/monster"
        try {
            const {data} = await axios.post(apiEndpoint, monster)
            setMonster(data)
            setIsNew(false)
            setEditable(false)
            nav(`/monsters/${data.id}`, {replace: true})
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setNameError(err.response.data?.error ?? 'A monster with this name already exists.')
            }
        }
    }

    function addAbility() {
        if (!selectedAbility) return
        const already = (monster.offensiveAbilities ?? []).some(a => a.ability.id === selectedAbility.id)
        if (already) return
        const entry: MonsterOffensiveAbility = {
            ability: selectedAbility, quantity: addQty, recharge_time: addRecharge,
        }
        setMonster({...monster, offensiveAbilities: [...(monster.offensiveAbilities ?? []), entry]})
        setSelectedAbility(null)
        setAddQty(1); setAddRecharge(0)
    }

    function removeAbility(abilityId: number) {
        setMonster({...monster, offensiveAbilities: (monster.offensiveAbilities ?? []).filter(a => a.ability.id !== abilityId)})
    }

    function updateOffensiveQty(abilityId: number, qty: number) {
        setMonster({...monster, offensiveAbilities: (monster.offensiveAbilities ?? []).map(a => a.ability.id === abilityId ? {...a, quantity: qty} : a)})
    }

    function updateOffensiveRecharge(abilityId: number, recharge: number) {
        setMonster({...monster, offensiveAbilities: (monster.offensiveAbilities ?? []).map(a => a.ability.id === abilityId ? {...a, recharge_time: recharge} : a)})
    }

    function updateOffensiveField(abilityId: number, field: keyof MonsterOffensiveAbility, value: any) {
        setMonster({...monster, offensiveAbilities: (monster.offensiveAbilities ?? []).map(a => a.ability.id === abilityId ? {...a, [field]: value} : a)})
    }

    function addDefensiveAbility() {
        if (!selectedDefensive) return
        const already = (monster.defensiveAbilities ?? []).some(a => a.ability.id === selectedDefensive.id)
        if (already) return
        const entry: MonsterDefensiveAbility = {
            ability: selectedDefensive, quantity: addDefQty,
        }
        setMonster({...monster, defensiveAbilities: [...(monster.defensiveAbilities ?? []), entry]})
        setSelectedDefensive(null)
        setAddDefQty(1)
    }

    function updateDefensiveField(abilityId: number, field: keyof MonsterDefensiveAbility, value: any) {
        setMonster({...monster, defensiveAbilities: (monster.defensiveAbilities ?? []).map(a => a.ability.id === abilityId ? {...a, [field]: value} : a)})
    }

    function removeDefensiveAbility(abilityId: number) {
        setMonster({...monster, defensiveAbilities: (monster.defensiveAbilities ?? []).filter(a => a.ability.id !== abilityId)})
    }

    function updateDefensiveQty(abilityId: number, qty: number) {
        setMonster({...monster, defensiveAbilities: (monster.defensiveAbilities ?? []).map(a => a.ability.id === abilityId ? {...a, quantity: qty} : a)})
    }

    function addMonstrousAbility() {
        if (!selectedMonstrous) return
        const already = (monster.monstrousAbilities ?? []).some(a => a.ability.id === selectedMonstrous.id)
        if (already) return
        const entry: EntityMonstrousAbility = {ability: selectedMonstrous}
        setMonster({...monster, monstrousAbilities: [...(monster.monstrousAbilities ?? []), entry]})
        setSelectedMonstrous(null)
    }

    function removeMonstrousAbility(abilityId: number) {
        setMonster({...monster, monstrousAbilities: (monster.monstrousAbilities ?? []).filter(a => a.ability.id !== abilityId)})
    }

    function handleMonstrousAbilityCreated(ability: MonstrousAbility) {
        refreshMonstrousAbilities()
        const entry: EntityMonstrousAbility = {ability}
        setMonster({...monster, monstrousAbilities: [...(monster.monstrousAbilities ?? []), entry]})
    }

    async function uploadImage(file: File) {
        if (!monster.id) return
        const formData = new FormData()
        formData.append('image', file)
        setImageUploading(true)
        try {
            const {data} = await axios.post(`/monsters/monster/${monster.id}/image`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            })
            setMonster({...monster, attributes: {...(monster.attributes ?? {}), image_url: data.image_url}})
        } finally {
            setImageUploading(false)
        }
    }

    function hasOffensiveDetails(a: MonsterOffensiveAbility): boolean {
        return !!(a.packet_only || a.strike || a.touchcast || a.selfcast || a.gaze || a.triggered_by || a.conditional_on || a.swarm)
    }

    function hasDefensiveDetails(a: MonsterDefensiveAbility): boolean {
        return !!(a.triggered_by || a.conditional_on || a.swarm)
    }

    function abilityChipColor(type: string): 'primary' | 'success' | 'error' {
        if (type === 'IMMUNITY') return 'primary'
        if (type === 'STRENGTH') return 'success'
        return 'error'
    }

    function typesAutocomplete() {
        let ac = new Array<{ label: string, id: number }>()
        types.forEach((type) => {
            ac.push({label: type.species.name + " - " + type.name, id: type.id as number})
        })
        return ac
    }

    function printCard() {
        const win = window.open('', '_blank', 'width=600,height=400')
        if (!win) return
        const html = document.createElement('html')
        const head = document.createElement('head')
        head.innerHTML = `<style>
            @page { size: 5in 3in; margin: 0; }
            body { margin: 0; padding: 0; background: white; }
            * { box-sizing: border-box; }
        </style>`
        const body = document.createElement('body')
        const root = document.createElement('div')
        body.appendChild(root)
        html.appendChild(head)
        html.appendChild(body)
        win.document.replaceChild(html, win.document.documentElement)

        // Render via a temporary React root
        import('react-dom/client').then(({createRoot}) => {
            const reactRoot = createRoot(root)
            reactRoot.render(<MonsterIndexCard monster={monster} carriers={carriers}/>)
            setTimeout(() => {
                win.focus()
                win.print()
            }, 300)
        })
    }

    function printManual() {
        const printedAt = new Date().toLocaleString();
        const win = window.open('', '_blank', 'width=850,height=1100')
        if (!win) return
        const html = document.createElement('html')
        const head = document.createElement('head')
        head.innerHTML = `<style>
            @page {
                size: 8.5in 11in;
                margin: 0.5in;
                @bottom-left {
                    content: "Printed: ${printedAt}";
                    font-family: Arial, sans-serif;
                    font-size: 9pt;
                    color: #666;
                }
            }
            body { margin: 0; padding: 0; background: white; }
            * { box-sizing: border-box; }
        </style>`
        const body = document.createElement('body')
        const root = document.createElement('div')
        body.appendChild(root)
        html.appendChild(head)
        html.appendChild(body)
        win.document.replaceChild(html, win.document.documentElement)

        import('react-dom/client').then(({createRoot}) => {
            const reactRoot = createRoot(root)
            reactRoot.render(<MonsterManualEntry monster={monster} carriers={carriers}/>)
            setTimeout(() => {
                win.focus()
                win.print()
            }, 500)
        })
    }

    function printCardBulk() {
        // Replicate needsDoubleSided logic from MonsterIndexCard
        const offCount = (monster.offensiveAbilities ?? []).length;
        const defCount = (monster.defensiveAbilities ?? []).length;
        const seenIds = new Set<number>();
        const monCount = [
            ...(monster.type?.species?.monstrousAbilities ?? []),
            ...(monster.type?.monstrousAbilities ?? []),
            ...(monster.monstrousAbilities ?? []),
        ].filter(e => { const id = e.ability.id as number; if (seenIds.has(id)) return false; seenIds.add(id); return true; }).length;
        const isDoubleSided = offCount > 6 || defCount > 6 || monCount > 8;

        const win = window.open('', '_blank', 'width=1100,height=850')
        if (!win) return
        const html = document.createElement('html')
        const head = document.createElement('head')
        head.innerHTML = `<style>
            @page { size: 11in 8.5in; margin: 0; }
            body { margin: 0; padding: 0; background: white; }
            * { box-sizing: border-box; }
        </style>`
        const body = document.createElement('body')
        const root = document.createElement('div')
        body.appendChild(root)
        html.appendChild(head)
        html.appendChild(body)
        win.document.replaceChild(html, win.document.documentElement)

        const gridStyle: React.CSSProperties = {display: "grid", gridTemplateColumns: "5in 5in", gridTemplateRows: "3in 3in", gap: "0.25in"};
        const cellStyle: React.CSSProperties = {width: "5in", height: "3in", overflow: "hidden"};

        const cardGrid = (side: "front" | "back") => (
            <div style={gridStyle}>
                {[0, 1, 2, 3].map(i => (
                    <div key={i} style={cellStyle}>
                        <MonsterIndexCard monster={monster} carriers={carriers} side={side}/>
                    </div>
                ))}
            </div>
        );

        const pageWrap = (content: React.ReactNode, pageBreak = false): React.ReactElement => (
            <div style={{width: "11in", height: "8.5in", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", pageBreakAfter: pageBreak ? "always" : "auto"}}>
                {content}
            </div>
        );

        import('react-dom/client').then(({createRoot}) => {
            const reactRoot = createRoot(root)
            reactRoot.render(isDoubleSided
                ? <>{pageWrap(cardGrid("front"), true)}{pageWrap(cardGrid("back"))}</>
                : pageWrap(cardGrid("front"))
            )
            setTimeout(() => {
                win.focus()
                win.print()
            }, 300)
        })
    }

    return <Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align={"center"} variant="h4" component="h4">{headLabel()}</Typography>

                    <Container sx={{textAlign: 'center'}}><ButtonGroup variant="contained" aria-label="Basic button group"
                                                             size="small"><Button
                        title="Edit" onClick={()=>{setEditable(true)}} disabled={editable}><EditIcon/></Button><Button title="Save" disabled={!editable}
                                                                                          onClick={saveMonster}><SaveIcon/></Button><Button
                        title="Delete Monster" disabled={isNew}><DeleteIcon/></Button><Button
                        title="Print Index Card" disabled={isNew} onClick={(e) => setPrintCardMenuAnchor(e.currentTarget)}><PrintIcon/></Button><Button
                        title="Print Monster Manual Entry" disabled={isNew} onClick={printManual}><MenuBookIcon/></Button></ButtonGroup></Container>
                    <Menu anchorEl={printCardMenuAnchor} open={Boolean(printCardMenuAnchor)} onClose={() => setPrintCardMenuAnchor(null)}>
                        <MenuItem onClick={() => { setPrintCardMenuAnchor(null); printCard(); }}>Individual</MenuItem>
                        <MenuItem onClick={() => { setPrintCardMenuAnchor(null); printCardBulk(); }}>Bulk (4 per sheet)</MenuItem>
                    </Menu>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}> <TextField fullWidth id='name' label='Name' disabled={!editable}
                                                      required
                                                      error={!!nameError}
                                                      helperText={nameError ?? ''}
                                                      defaultValue={monster.name}
                                                      onChange={(ev) => {
                                                          monster.name = ev.target.value
                                                          if (nameError) setNameError(null)
                                                      }}/>
                        </Grid>
                        <Grid item xs={6}><Autocomplete
                            disablePortal
                            disabled={!editable}
                            options={typesAutocomplete()}
                            fullWidth
                            value={monster.type ? {label: monster.type.species.name + " - " + monster.type.name, id: monster.type.id as number} : null}
                            onChange={(_, newValue) => {
                                if (newValue) {
                                    const selected = types.find(t => t.id === newValue.id) as MonsterType
                                    setMonster({...monster, type: selected})
                                }
                            }}
                            renderInput={(params) => <TextField required {...params} label="Type"/>}
                        /></Grid>
                        <Grid item xs={6}>
                            <FormControlLabel control={<Checkbox
                                disabled={!editable}
                                id='unique'
                                defaultChecked={monster.unique}
                                onChange={(ev) => {
                                    monster.unique = ev.target.checked
                                }}
                                inputProps={{'aria-label': 'controlled'}}
                            />} label="Unique Monster"/>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel control={<Checkbox
                                disabled={!editable}
                                id='dedicated_marshal'
                                defaultChecked={monster.dedicated_marshal ?? false}
                                onChange={(ev) => {
                                    monster.dedicated_marshal = ev.target.checked
                                }}
                                inputProps={{'aria-label': 'controlled'}}
                            />} label="Dedicated Marshal"/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Species</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='speciesName' label='Species Name' disabled
                                       value={monster.type?.species.name ?? ''}
                            /></Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='speciesDescription' label='Species Description' disabled
                                       multiline
                                       minRows={5}
                                       value={monster.type?.species.description ?? ''}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Type</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='typeName' label='Type Name' disabled
                                       value={monster.type?.name ?? ''}
                            /></Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='typeDescription' label='Type Description' disabled
                                       multiline
                                       minRows={2}
                                       value={monster.type?.description ?? ''}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth id='typeCombat' label='Combat Role' disabled
                                       value={monster.type?.combat_role ?? ''}
                            /></Grid>
                        <Grid item xs={6}>
                            <FormControlLabel control={<Checkbox
                                disabled
                                id='TypeGeneralUse'
                                checked={monster.type?.general_use ?? false}
                                inputProps={{'aria-label': 'controlled'}}
                            />} label="General Use"/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Combat Stats</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField fullWidth label='Body' type='number' disabled={!editable}
                                       defaultValue={monster.attributes?.body}
                                       onChange={(ev) => {
                                           monster.attributes.body = parseInt(ev.target.value)
                                       }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label='Armor' type='number' disabled={!editable}
                                       defaultValue={monster.attributes?.armor}
                                       onChange={(ev) => {
                                           monster.attributes.armor = parseInt(ev.target.value)
                                       }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField fullWidth label='Damage' type='number' disabled={!editable}
                                       defaultValue={monster.attributes?.damage}
                                       onChange={(ev) => {
                                           monster.attributes.damage = parseInt(ev.target.value)
                                       }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl fullWidth disabled={!editable}>
                                <InputLabel id='carrier-label'>Carrier</InputLabel>
                                <Select
                                    labelId='carrier-label'
                                    label='Carrier'
                                    defaultValue={monster.attributes?.carrierId ?? ''}
                                    onChange={(ev) => {
                                        monster.attributes.carrierId = ev.target.value as number
                                    }}
                                >
                                    {carriers.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label='ACE' disabled={!editable}
                                       defaultValue={monster.attributes?.ace}
                                       onChange={(ev) => {
                                           monster.attributes.ace = ev.target.value
                                       }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Weaponry' disabled={!editable}
                                       multiline minRows={3}
                                       defaultValue={monster.attributes?.weaponry}
                                       onChange={(ev) => {
                                           monster.attributes.weaponry = ev.target.value
                                       }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label='Total Ability Cost' disabled
                                       value={
                                           (monster.offensiveAbilities ?? []).reduce((sum, a) => sum + a.ability.cost * a.quantity, 0) +
                                           (monster.defensiveAbilities ?? []).reduce((sum, a) => sum + a.ability.cost * a.quantity, 0)
                                       }
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Behavior</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={!editable}>
                                <InputLabel id='intelligence-label'>Intelligence</InputLabel>
                                <Select
                                    labelId='intelligence-label'
                                    label='Intelligence'
                                    value={monster.intelligenceId ?? ''}
                                    onChange={(ev) => {
                                        setMonster({...monster, intelligenceId: ev.target.value as number || null})
                                    }}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {intelligenceLevels.map(l => (
                                        <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {monster.intelligenceId && (() => {
                            const desc = intelligenceLevels.find(l => l.id === monster.intelligenceId)?.description
                            return desc ? (
                                <Grid item xs={12}>
                                    <TextField fullWidth label='Intelligence Description' disabled multiline
                                               value={desc}
                                    />
                                </Grid>
                            ) : null
                        })()}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label='Social Behavior' disabled={!editable}
                                       multiline
                                       minRows={9}
                                       defaultValue={monster.attributes?.social_behavior}
                                       onChange={(ev) => {
                                           monster.attributes.social_behavior = ev.target.value
                                       }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label='Combat Behavior' disabled={!editable}
                                       multiline minRows={9}
                                       defaultValue={monster.attributes?.combat_behavior}
                                       onChange={(ev) => {
                                           monster.attributes.combat_behavior = ev.target.value
                                       }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Physical Appearance</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={3} sx={{textAlign: 'center'}}>
                            <img
                                src={monster.attributes?.image_url ?? '/monster-placeholder.png'}
                                alt={monster.name}
                                style={{maxWidth: '100%', maxHeight: 250, objectFit: 'contain', borderRadius: 4}}
                            />
                        </Grid>
                        <Grid item xs={9}>
                            <TextField fullWidth label='Physical Appearance' disabled={!editable}
                                       multiline minRows={8}
                                       defaultValue={monster.attributes?.physical_appearance}
                                       onChange={(ev) => {
                                           monster.attributes.physical_appearance = ev.target.value
                                       }}
                            />
                        </Grid>
                        {editable && !isNew && (
                            <Grid item xs={3}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<UploadIcon/>}
                                    disabled={imageUploading}
                                >
                                    {imageUploading ? 'Uploading…' : monster.attributes?.image_url ? 'Replace Image' : 'Upload Image'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(ev) => {
                                            const file = ev.target.files?.[0]
                                            if (file) uploadImage(file)
                                            ev.target.value = ''
                                        }}
                                    />
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Offensive Abilities</Typography>
                    <Grid container spacing={1} sx={{mt: 0.5}}>
                        <Grid item xs={6}>
                            <Autocomplete
                                disabled={!editable}
                                options={abilityCatalog}
                                getOptionLabel={(a) => `${a.name} — ${a.call} (cost: ${a.cost})`}
                                value={selectedAbility}
                                onChange={(_, value) => setSelectedAbility(value)}
                                renderInput={(params) => <TextField {...params} label="Ability" size="small"/>}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField fullWidth label="Qty" type="number" size="small"
                                       disabled={!editable}
                                       inputProps={{min: 1}}
                                       value={addQty}
                                       onChange={(ev) => setAddQty(Math.max(1, parseInt(ev.target.value) || 1))}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField fullWidth label="Recharge (s)" type="number" size="small"
                                       disabled={!editable}
                                       inputProps={{min: 0}}
                                       value={addRecharge}
                                       onChange={(ev) => setAddRecharge(Math.max(0, parseInt(ev.target.value) || 0))}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button fullWidth variant="contained" disabled={!editable || !selectedAbility}
                                    onClick={addAbility}>Add</Button>
                        </Grid>
                        {(monster.offensiveAbilities ?? []).length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">No abilities assigned</Typography>
                            </Grid>
                        )}
                        {(monster.offensiveAbilities ?? []).map((a, i) => (
                            <Grid item xs={12} key={a.id ?? i}>
                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                    <TextField size="small" disabled sx={{flex: 1}}
                                               value={`${a.ability.name} — ${a.ability.call}`}
                                    />
                                    <TextField label="Qty" type="number" size="small" sx={{width: 70}}
                                               disabled={!editable} inputProps={{min: 1}}
                                               value={a.quantity}
                                               onChange={(ev) => updateOffensiveQty(a.ability.id as number, Math.max(1, parseInt(ev.target.value) || 1))}
                                    />
                                    <TextField label="Recharge (s)" type="number" size="small" sx={{width: 110}}
                                               disabled={!editable} inputProps={{min: 0}}
                                               value={a.recharge_time}
                                               onChange={(ev) => updateOffensiveRecharge(a.ability.id as number, Math.max(0, parseInt(ev.target.value) || 0))}
                                    />
                                    <Tooltip title={hasOffensiveDetails(a) ? "Details (set)" : "Details"}>
                                        <IconButton size="small" color={hasOffensiveDetails(a) ? 'primary' : 'default'}
                                                    onClick={() => setOffDetailAbility(a)}>
                                            <TuneIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove Ability"><span>
                                        <IconButton disabled={!editable} size="small" color="error"
                                                    onClick={() => removeAbility(a.ability.id as number)}>
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </span></Tooltip>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Defensive Abilities</Typography>
                    <Grid container spacing={1} sx={{mt: 0.5}}>
                        <Grid item xs={8}>
                            <Autocomplete
                                disabled={!editable}
                                options={defensiveCatalog}
                                getOptionLabel={(a) => `${a.name} — ${a.call} (cost: ${a.cost})`}
                                value={selectedDefensive}
                                onChange={(_, value) => setSelectedDefensive(value)}
                                renderInput={(params) => <TextField {...params} label="Ability" size="small"/>}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField fullWidth label="Qty" type="number" size="small"
                                       disabled={!editable}
                                       inputProps={{min: 1}}
                                       value={addDefQty}
                                       onChange={(ev) => setAddDefQty(Math.max(1, parseInt(ev.target.value) || 1))}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button fullWidth variant="contained" disabled={!editable || !selectedDefensive}
                                    onClick={addDefensiveAbility}>Add</Button>
                        </Grid>
                        {(monster.defensiveAbilities ?? []).length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">No abilities assigned</Typography>
                            </Grid>
                        )}
                        {(monster.defensiveAbilities ?? []).map((a, i) => (
                            <Grid item xs={12} key={a.id ?? i}>
                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                    <TextField size="small" disabled sx={{flex: 1}}
                                               value={`${a.ability.name} — ${a.ability.call}`}
                                    />
                                    <TextField label="Qty" type="number" size="small" sx={{width: 70}}
                                               disabled={!editable} inputProps={{min: 1}}
                                               value={a.quantity}
                                               onChange={(ev) => updateDefensiveQty(a.ability.id as number, Math.max(1, parseInt(ev.target.value) || 1))}
                                    />
                                    <Tooltip title={hasDefensiveDetails(a) ? "Details (set)" : "Details"}>
                                        <IconButton size="small" color={hasDefensiveDetails(a) ? 'primary' : 'default'}
                                                    onClick={() => setDefDetailAbility(a)}>
                                            <TuneIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove Ability"><span>
                                        <IconButton disabled={!editable} size="small" color="error"
                                                    onClick={() => removeDefensiveAbility(a.ability.id as number)}>
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </span></Tooltip>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Monstrous Abilities</Typography>
                    <Grid container spacing={1} sx={{mt: 0.5}}>

                        {/* Own abilities */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Assigned to this Monster</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                disabled={!editable}
                                options={monstrousCatalog.filter(a => !(monster.monstrousAbilities ?? []).some(e => e.ability.id === a.id))}
                                getOptionLabel={(a) => `${a.name} — ${a.call} (${a.type})`}
                                value={selectedMonstrous}
                                onChange={(_, value) => setSelectedMonstrous(value)}
                                renderInput={(params) => <TextField {...params} label="Ability" size="small"/>}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Button fullWidth variant="contained" disabled={!editable || !selectedMonstrous}
                                    onClick={addMonstrousAbility}>Add</Button>
                        </Grid>
                        <Grid item xs={3}>
                            <Button fullWidth variant="outlined" disabled={!editable}
                                    onClick={() => setCreateMonstrousOpen(true)}>Create New</Button>
                        </Grid>
                        {(monster.monstrousAbilities ?? []).length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">No monstrous abilities assigned</Typography>
                            </Grid>
                        )}
                        {(monster.monstrousAbilities ?? []).map((a, i) => (
                            <Grid item xs={12} key={a.id ?? i} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Chip label={a.ability.type} color={abilityChipColor(a.ability.type)} size="small"/>
                                <Typography variant="body2" sx={{flex: 1}}>{a.ability.name} — {a.ability.call}</Typography>
                                <Tooltip title="Remove Ability"><span>
                                    <IconButton disabled={!editable} size="small" color="error"
                                                onClick={() => removeMonstrousAbility(a.ability.id as number)}>
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </span></Tooltip>
                            </Grid>
                        ))}

                        {/* Inherited from type */}
                        {(monster.type?.monstrousAbilities ?? []).length > 0 && <>
                            <Grid item xs={12} sx={{mt: 1}}>
                                <Typography variant="subtitle2">Inherited from Type (read-only)</Typography>
                            </Grid>
                            {(monster.type.monstrousAbilities ?? []).map((a, i) => (
                                <Grid item xs={12} key={a.id ?? i} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Chip label={a.ability.type} color={abilityChipColor(a.ability.type)} size="small"/>
                                    <Typography variant="body2">{a.ability.name} — {a.ability.call}</Typography>
                                </Grid>
                            ))}
                        </>}

                        {/* Inherited from species */}
                        {(monster.type?.species?.monstrousAbilities ?? []).length > 0 && <>
                            <Grid item xs={12} sx={{mt: 1}}>
                                <Typography variant="subtitle2">Inherited from Species (read-only)</Typography>
                            </Grid>
                            {(monster.type.species.monstrousAbilities ?? []).map((a, i) => (
                                <Grid item xs={12} key={a.id ?? i} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Chip label={a.ability.type} color={abilityChipColor(a.ability.type)} size="small"/>
                                    <Typography variant="body2">{a.ability.name} — {a.ability.call}</Typography>
                                </Grid>
                            ))}
                        </>}

                    </Grid>
                </Paper>
            </Grid>

        </Grid>

        {/* Offensive ability details dialog */}
        {offDetailAbility && (
            <Dialog open onClose={() => setOffDetailAbility(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{offDetailAbility.ability.name} — Details</DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, pt: 1}}>
                        <FormControlLabel label="Packet Only" control={<Checkbox disabled={!editable}
                            checked={!!offDetailAbility.packet_only}
                            onChange={ev => {
                                updateOffensiveField(offDetailAbility.ability.id as number, 'packet_only', ev.target.checked)
                                setOffDetailAbility({...offDetailAbility, packet_only: ev.target.checked})
                            }}/>}/>
                        <FormControlLabel label="Strike" control={<Checkbox disabled={!editable}
                            checked={!!offDetailAbility.strike}
                            onChange={ev => {
                                updateOffensiveField(offDetailAbility.ability.id as number, 'strike', ev.target.checked)
                                setOffDetailAbility({...offDetailAbility, strike: ev.target.checked})
                            }}/>}/>
                        <FormControlLabel label="Touchcast" control={<Checkbox disabled={!editable}
                            checked={!!offDetailAbility.touchcast}
                            onChange={ev => {
                                updateOffensiveField(offDetailAbility.ability.id as number, 'touchcast', ev.target.checked)
                                setOffDetailAbility({...offDetailAbility, touchcast: ev.target.checked})
                            }}/>}/>
                        <FormControlLabel label="Selfcast" control={<Checkbox disabled={!editable}
                            checked={!!offDetailAbility.selfcast}
                            onChange={ev => {
                                updateOffensiveField(offDetailAbility.ability.id as number, 'selfcast', ev.target.checked)
                                setOffDetailAbility({...offDetailAbility, selfcast: ev.target.checked})
                            }}/>}/>
                        <FormControlLabel label="Gaze" control={<Checkbox disabled={!editable}
                            checked={!!offDetailAbility.gaze}
                            onChange={ev => {
                                updateOffensiveField(offDetailAbility.ability.id as number, 'gaze', ev.target.checked)
                                setOffDetailAbility({...offDetailAbility, gaze: ev.target.checked})
                            }}/>}/>
                    </Box>
                    <Box sx={{display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap'}}>
                        <TextField label="Triggered By" disabled={!editable} sx={{flex: 1, minWidth: 150}}
                                   value={offDetailAbility.triggered_by ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value || null
                                       updateOffensiveField(offDetailAbility.ability.id as number, 'triggered_by', v)
                                       setOffDetailAbility({...offDetailAbility, triggered_by: v})
                                   }}/>
                        <TextField label="Conditional On" disabled={!editable} sx={{flex: 1, minWidth: 150}}
                                   value={offDetailAbility.conditional_on ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value || null
                                       updateOffensiveField(offDetailAbility.ability.id as number, 'conditional_on', v)
                                       setOffDetailAbility({...offDetailAbility, conditional_on: v})
                                   }}/>
                        <TextField label="Swarm" type="number" disabled={!editable} sx={{width: 90}}
                                   inputProps={{min: 1}}
                                   value={offDetailAbility.swarm ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value ? parseInt(ev.target.value) : null
                                       updateOffensiveField(offDetailAbility.ability.id as number, 'swarm', v)
                                       setOffDetailAbility({...offDetailAbility, swarm: v})
                                   }}/>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOffDetailAbility(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        )}

        <CreateMonstrousAbilityDialog
            open={createMonstrousOpen}
            onClose={() => setCreateMonstrousOpen(false)}
            onCreated={handleMonstrousAbilityCreated}
        />

        {/* Defensive ability details dialog */}
        {defDetailAbility && (
            <Dialog open onClose={() => setDefDetailAbility(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{defDetailAbility.ability.name} — Details</DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap'}}>
                        <TextField label="Triggered By" disabled={!editable} sx={{flex: 1, minWidth: 150}}
                                   value={defDetailAbility.triggered_by ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value || null
                                       updateDefensiveField(defDetailAbility.ability.id as number, 'triggered_by', v)
                                       setDefDetailAbility({...defDetailAbility, triggered_by: v})
                                   }}/>
                        <TextField label="Conditional On" disabled={!editable} sx={{flex: 1, minWidth: 150}}
                                   value={defDetailAbility.conditional_on ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value || null
                                       updateDefensiveField(defDetailAbility.ability.id as number, 'conditional_on', v)
                                       setDefDetailAbility({...defDetailAbility, conditional_on: v})
                                   }}/>
                        <TextField label="Swarm" type="number" disabled={!editable} sx={{width: 90}}
                                   inputProps={{min: 1}}
                                   value={defDetailAbility.swarm ?? ''}
                                   onChange={ev => {
                                       const v = ev.target.value ? parseInt(ev.target.value) : null
                                       updateDefensiveField(defDetailAbility.ability.id as number, 'swarm', v)
                                       setDefDetailAbility({...defDetailAbility, swarm: v})
                                   }}/>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDefDetailAbility(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        )}

    </Fragment>
}
